import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  getDocs, 
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { newMessageEmail } from './email';

// Types for messaging
export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderEmail: string;
  content: string;
  timestamp: Timestamp;
  read: boolean;
  type: 'text' | 'image' | 'audio' | 'system';
  // Optional reply reference
  replyToId?: string | null;
}

export interface Conversation {
  id: string;
  participants: string[]; // Array of user IDs
  participantDetails: {
    [userId: string]: {
      name: string;
      email: string;
      avatar?: string;
    };
  };
  lastMessage?: {
    content: string;
    timestamp: Timestamp;
    senderId: string;
    type?: 'text' | 'image' | 'audio' | 'system';
  };
  listingId?: string; // Optional: if conversation is about a specific listing
  listingTitle?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  unreadCount: {
    [userId: string]: number;
  };
  // Tracks the last time we emailed each participant about a new message in this
  // conversation. Used to throttle "new message" notifications (5-min window).
  lastNotifiedAt?: {
    [userId: string]: Timestamp;
  };
}

// Create a new conversation
export const createConversation = async (
  participantId: string,
  participantName: string,
  participantEmail: string,
  listingId?: string,
  listingTitle?: string
): Promise<string> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User not authenticated');

  // Check if conversation already exists between these users
  const existingConversation = await findExistingConversation(currentUser.uid, participantId);
  if (existingConversation) {
    return existingConversation.id;
  }

  const conversationData: Omit<Conversation, 'id'> = {
    participants: [currentUser.uid, participantId],
    participantDetails: {
      [currentUser.uid]: {
        name: currentUser.displayName || 'Unknown User',
        email: currentUser.email || '',
      },
      [participantId]: {
        name: participantName,
        email: participantEmail,
      }
    },
    listingId,
    listingTitle,
    createdAt: serverTimestamp() as Timestamp,
    updatedAt: serverTimestamp() as Timestamp,
    unreadCount: {
      [currentUser.uid]: 0,
      [participantId]: 0
    }
  };

  const docRef = await addDoc(collection(db, 'conversations'), conversationData);
  return docRef.id;
};

// Find existing conversation between two users
export const findExistingConversation = async (
  userId1: string, 
  userId2: string
): Promise<Conversation | null> => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId1)
  );

  const querySnapshot = await getDocs(q);
  
  for (const doc of querySnapshot.docs) {
    const conversation = { id: doc.id, ...doc.data() } as Conversation;
    if (conversation.participants.includes(userId2)) {
      return conversation;
    }
  }
  
  return null;
};

// Send a message
export const sendMessage = async (
  conversationId: string,
  content: string,
  type: 'text' | 'image' | 'audio' | 'system' = 'text',
  replyToId?: string | null
): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User not authenticated');

  const batch = writeBatch(db);

  // Add the message
  const messageData: Omit<Message, 'id'> = {
    conversationId,
    senderId: currentUser.uid,
    senderName: currentUser.displayName || 'Unknown User',
    senderEmail: currentUser.email || '',
    content,
    timestamp: serverTimestamp() as Timestamp,
    read: false,
    type,
    replyToId: replyToId || null
  };

  const messageRef = doc(collection(db, 'messages'));
  batch.set(messageRef, messageData);

  // Update conversation with last message and increment unread count
  const conversationRef = doc(db, 'conversations', conversationId);
  
  // Get current conversation to update unread counts
  const conversationDoc = await getDocs(query(
    collection(db, 'conversations'),
    where('__name__', '==', conversationId)
  ));

  // Decide which participants to notify by email and update lastNotifiedAt
  // BEFORE the commit, so the throttle check is atomic-ish with the send itself.
  const recipientsToEmail: { uid: string; email: string; name: string }[] = [];
  let conversationData: Conversation | null = null;

  if (!conversationDoc.empty) {
    const conversation = conversationDoc.docs[0].data() as Conversation;
    conversationData = conversation;
    const updatedUnreadCount = { ...conversation.unreadCount };

    // Increment unread count for all participants except sender
    conversation.participants.forEach(participantId => {
      if (participantId !== currentUser.uid) {
        updatedUnreadCount[participantId] = (updatedUnreadCount[participantId] || 0) + 1;
      }
    });

    // 5-minute throttle: only email a recipient if we haven't emailed them in this
    // conversation in the last 5 minutes (prevents spamming for rapid-fire messages).
    const now = Date.now();
    const FIVE_MINUTES = 5 * 60 * 1000;
    const updatedNotifiedAt = { ...(conversation.lastNotifiedAt || {}) };
    conversation.participants.forEach((participantId) => {
      if (participantId === currentUser.uid) return;
      const last: any = (conversation.lastNotifiedAt || {})[participantId];
      const lastMs = last?.toMillis ? last.toMillis() : (typeof last === 'number' ? last : 0);
      if (!lastMs || (now - lastMs) >= FIVE_MINUTES) {
        const details = conversation.participantDetails?.[participantId];
        if (details?.email) {
          recipientsToEmail.push({
            uid: participantId,
            email: details.email,
            name: details.name || 'there',
          });
          (updatedNotifiedAt as any)[participantId] = serverTimestamp();
        }
      }
    });

    batch.update(conversationRef, {
      lastMessage: {
        content,
        timestamp: serverTimestamp(),
        senderId: currentUser.uid,
        type,
      },
      updatedAt: serverTimestamp(),
      unreadCount: updatedUnreadCount,
      lastNotifiedAt: updatedNotifiedAt,
    });
  }

  await batch.commit();

  // Fire-and-forget email notifications. Don't block the message send if email fails.
  if (recipientsToEmail.length > 0) {
    const senderName = currentUser.displayName || conversationData?.participantDetails?.[currentUser.uid]?.name || 'A user';
    Promise.all(
      recipientsToEmail.map((r) =>
        newMessageEmail({
          recipientEmail: r.email,
          senderName,
          preview: content,
          conversationId,
          type,
        })
      )
    ).catch((e) => console.warn('new-message email notify failed', e));
  }
};

// Subscribe to messages in a conversation
export const subscribeToMessages = (
  conversationId: string,
  callback: (messages: Message[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'messages'),
    where('conversationId', '==', conversationId),
    orderBy('timestamp', 'asc')
  );

  return onSnapshot(q, (querySnapshot) => {
    const messages: Message[] = [];
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() } as Message);
    });
    callback(messages);
  });
};

// Subscribe to user's conversations
export const subscribeToConversations = (
  userId: string,
  callback: (conversations: Conversation[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId)
  );

  return onSnapshot(q, (querySnapshot) => {
    const conversations: Conversation[] = [];
    querySnapshot.forEach((doc) => {
      conversations.push({ id: doc.id, ...doc.data() } as Conversation);
    });
    conversations.sort((a, b) => {
      const aTime = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
      const bTime = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
      return bTime - aTime;
    });
    callback(conversations);
  });
};

// Mark messages as read
export const markMessagesAsRead = async (
  conversationId: string,
  userId: string
): Promise<void> => {
  const batch = writeBatch(db);

  // Mark all unread messages in the conversation as read
  const messagesQuery = query(
    collection(db, 'messages'),
    where('conversationId', '==', conversationId),
    where('senderId', '!=', userId),
    where('read', '==', false)
  );

  const messagesSnapshot = await getDocs(messagesQuery);
  messagesSnapshot.forEach((doc) => {
    batch.update(doc.ref, { read: true });
  });

  // Reset unread count for this user in the conversation
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationDoc = await getDocs(query(
    collection(db, 'conversations'),
    where('__name__', '==', conversationId)
  ));

  if (!conversationDoc.empty) {
    const conversation = conversationDoc.docs[0].data() as Conversation;
    const updatedUnreadCount = { ...conversation.unreadCount };
    updatedUnreadCount[userId] = 0;

    batch.update(conversationRef, {
      unreadCount: updatedUnreadCount
    });
  }

  await batch.commit();
};

// Get total unread messages count for a user
export const getTotalUnreadCount = async (userId: string): Promise<number> => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId)
  );

  const querySnapshot = await getDocs(q);
  let totalUnread = 0;

  querySnapshot.forEach((doc) => {
    const conversation = doc.data() as Conversation;
    totalUnread += conversation.unreadCount[userId] || 0;
  });

  return totalUnread;
};

// Delete a conversation (soft delete - mark as deleted)
export const deleteConversation = async (conversationId: string): Promise<void> => {
  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, {
    deletedAt: serverTimestamp()
  });
};

// Search conversations by participant name or listing title
export const searchConversations = async (
  userId: string,
  searchTerm: string
): Promise<Conversation[]> => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', userId)
  );

  const querySnapshot = await getDocs(q);
  const conversations: Conversation[] = [];

  querySnapshot.forEach((doc) => {
    const conversation = { id: doc.id, ...doc.data() } as Conversation;
    
    // Search in participant names or listing title
    const searchLower = searchTerm.toLowerCase();
    const matchesParticipant = Object.values(conversation.participantDetails).some(
      participant => participant.name.toLowerCase().includes(searchLower)
    );
    const matchesListing = conversation.listingTitle?.toLowerCase().includes(searchLower);

    if (matchesParticipant || matchesListing) {
      conversations.push(conversation);
    }
  });

  return conversations.sort((a, b) => 
    b.updatedAt.toMillis() - a.updatedAt.toMillis()
  );
};